import React, { useEffect, useState } from "react";
import { fetchAuthSession } from "aws-amplify/auth";

const CAMERAS_API =
  "https://f0usr04663.execute-api.us-east-1.amazonaws.com/cameras";

type Camera = {
  cameraId: string;
  host: string;
  httpPort?: number;
  username?: string;
  updatedAt?: string;
  onvif?: {
    port?: number;
    profileToken?: string;
  };
  [key: string]: unknown;
};

type Props = {
  camera: Camera;
  onUpdateCamera: (camera: any) => void;
};

function normalizeHost(input: string) {
  return input
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/+$/, "");
}

function isValidPort(value: number) {
  return Number.isInteger(value) && value >= 1 && value <= 65535;
}

async function getIdTokenOrThrow() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error("Your authentication session expired. Sign in again.");
  }
  return token;
}

async function readResponse(response: Response) {
  const text = await response.text();
  try {
    return { text, json: text ? JSON.parse(text) : null };
  } catch {
    return { text, json: null };
  }
}

export default function OnvifConnectionTab({ camera, onUpdateCamera }: Props) {
  const storedPort = Number(camera.onvif?.port ?? camera.httpPort ?? 80);
  const initialPort = isValidPort(storedPort) ? storedPort : 80;
  const [host, setHost] = useState(camera.host || "");
  const [onvifPort, setOnvifPort] = useState(initialPort);
  const [username, setUsername] = useState(camera.username || "");
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const nextPort = Number(camera.onvif?.port ?? camera.httpPort ?? 80);
    setHost(camera.host || "");
    setOnvifPort(isValidPort(nextPort) ? nextPort : 80);
    setUsername(camera.username || "");
    setNewPassword("");
    setMessage("");
    setIsError(false);
  }, [camera.cameraId, camera.host, camera.httpPort, camera.onvif?.port, camera.username]);

  async function saveConnection() {
    const normalizedHost = normalizeHost(host);
    const normalizedUsername = username.trim();

    if (!normalizedHost || !isValidPort(onvifPort) || !normalizedUsername) {
      setIsError(true);
      setMessage("Enter a host, valid ONVIF port, and username.");
      return;
    }

    setSaving(true);
    setIsError(false);
    setMessage("Saving ONVIF connection...");

    try {
      const idToken = await getIdTokenOrThrow();
      const credentialsChanged =
        normalizedUsername !== String(camera.username || "").trim() ||
        Boolean(newPassword);
      const payload: Record<string, unknown> = {
        host: normalizedHost,
        onvif: {
          port: onvifPort,
        },
      };

      if (credentialsChanged) {
        payload.credentials = {
          username: normalizedUsername,
          ...(newPassword ? { password: newPassword } : {}),
        };
      }

      const response = await fetch(
        `${CAMERAS_API}/${encodeURIComponent(camera.cameraId)}`,
        {
          method: "PATCH",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const { text, json } = await readResponse(response);
      if (!response.ok) {
        throw new Error(
          `${response.status}: ${
            json?.message || json?.error || text || response.statusText ||
            "Unable to update ONVIF connection."
          }`,
        );
      }

      const updatedCamera = {
        ...camera,
        ...(json?.camera || {}),
        host: normalizedHost,
        username: normalizedUsername,
        onvif: {
          ...(camera.onvif || {}),
          ...(json?.camera?.onvif || {}),
          port: onvifPort,
        },
        updatedAt: json?.camera?.updatedAt || new Date().toISOString(),
      };
      onUpdateCamera(updatedCamera);
      setNewPassword("");
      setMessage(
        json?.restartRequired
          ? "ONVIF connection saved. Live and recording ingestion restarted with the new connection."
          : "ONVIF connection saved successfully.",
      );
    } catch (error) {
      setIsError(true);
      setMessage(
        `Unable to save ONVIF connection: ${
          error instanceof Error ? error.message : "Unexpected error"
        }`,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      style={{
        display: "grid",
        gap: 20,
        padding: 22,
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        background: "#ffffff",
      }}
    >
      <div>
        <div
          style={{
            color: "#2563eb",
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".08em",
            textTransform: "uppercase",
          }}
        >
          Camera connection
        </div>
        <h3 style={{ margin: "5px 0 7px", color: "#0f172a" }}>
          ONVIF Connection
        </h3>
        <p style={{ margin: 0, color: "#64748b", lineHeight: 1.55 }}>
          Update the network address and ONVIF credentials used by Qumra. The
          current password is never displayed; leave the password field blank to
          keep it unchanged.
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
          gap: 16,
        }}
      >
        <Field label="Host / public IP *">
          <input
            aria-label="Host / public IP"
            value={host}
            onChange={(event) => setHost(event.target.value)}
            disabled={saving}
            style={inputStyle}
          />
        </Field>

        <Field label="ONVIF port *">
          <input
            aria-label="ONVIF port"
            type="number"
            min={1}
            max={65535}
            value={onvifPort}
            onChange={(event) => setOnvifPort(Number(event.target.value))}
            disabled={saving}
            style={inputStyle}
          />
        </Field>

        <Field label="Username *">
          <input
            aria-label="ONVIF username"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            autoComplete="username"
            disabled={saving}
            style={inputStyle}
          />
        </Field>

        <Field label="New password">
          <input
            aria-label="New ONVIF password"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            placeholder="Leave blank to keep current password"
            autoComplete="new-password"
            disabled={saving}
            style={inputStyle}
          />
        </Field>
      </div>

      {message && (
        <div
          role={isError ? "alert" : "status"}
          style={{
            padding: "11px 13px",
            border: `1px solid ${isError ? "#fecaca" : "#bbf7d0"}`,
            borderRadius: 10,
            color: isError ? "#b91c1c" : "#166534",
            background: isError ? "#fef2f2" : "#f0fdf4",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          {message}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="button"
          onClick={() => void saveConnection()}
          disabled={saving}
          style={{
            minHeight: 42,
            padding: "10px 17px",
            border: 0,
            borderRadius: 11,
            color: "#ffffff",
            background: "#111827",
            fontWeight: 800,
            cursor: saving ? "not-allowed" : "pointer",
            opacity: saving ? 0.6 : 1,
          }}
        >
          {saving ? "Saving..." : "Save ONVIF Connection"}
        </button>
      </div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: "grid", gap: 8, color: "#334155", fontWeight: 800 }}>
      <span>{label}</span>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 46,
  boxSizing: "border-box",
  padding: "10px 13px",
  border: "1px solid #cbd5e1",
  borderRadius: 11,
  color: "#0f172a",
  background: "#ffffff",
  font: "inherit",
  fontWeight: 600,
};
